import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import './style.scss';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AdminPageLayout from '../../../layout/AdminPageLayout';
import { useAppDispatch } from '../../../store';
import Headline from '../../../ui/Headline';
import { setIsModalOpen, setModalContent, setModalContentProps, } from '../../../store/slices/global';
import { MODAL_CONTENT } from '../../../app.config.vars/modalContent';
import Loader from '../../../ui/Loader';
import Button from '../../../ui/Button';
import PlusIcon from '../../../assets/icons/plus.svg';
import ButtonsGroup from '../../../ui/ButtonsGroup';
import { activateAdminRally, getAdminRally } from '../../../api/admin/rallies';
import { useNavigate, useParams } from 'react-router-dom';
import AsideContentLayout from '../../../layout/AsideContentLayout';
import ShareQR from '../../../components/ShareQR';
import AdminTasksList from '../../../modules/AdminTasksList';
import { getAdminTasks, sortTasks } from '../../../api/admin/tasks';
import { generateIdRoutePath } from '../../../helpers';
import { APP_ROUTES } from '../../../app.config.vars/routes';
import { useSelector } from 'react-redux';
import { selectSaveSortingChanges, selectSaveSortingTrigger, selectTaskWasDeleted, setSaveSortingChanges, setSaveSortingTrigger, setTaskWasDeleted, } from '../../../store/adminSlices/rallies';
export const ManageTasksPage = () => {
    const { id: rallyId } = useParams();
    const { i18n, t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [rally, setRally] = useState(null);
    const [tasks, setTasks] = useState(null);
    const [sortedTasks, setSortedTasks] = useState(null);
    const [isSortingChanged, setIsSortingChanged] = useState(false);
    const [submitSaveSortingStatus, setSubmitSaveSortingStatus] = useState(null);
    const taskWasDeleted = useSelector(selectTaskWasDeleted);
    const saveSortingChanges = useSelector(selectSaveSortingChanges);
    const saveSortingTrigger = useSelector(selectSaveSortingTrigger);
    const fetchData = async () => {
        try {
            const rally = await getAdminRally(rallyId);
            const tasks = await getAdminTasks(rallyId);
            setRally(rally);
            setTasks(tasks);
        }
        catch (error) {
            dispatch(setIsModalOpen(true));
            dispatch(setModalContent(MODAL_CONTENT.somethingWentWrong.key));
        }
    };
    useEffect(() => {
        fetchData();
    }, [i18n.language]);
    useEffect(() => {
        if (taskWasDeleted) {
            setIsSortingChanged(false);
            setSortedTasks(null);
            fetchData();
            dispatch(setTaskWasDeleted(false));
        }
    }, [taskWasDeleted]);
    const saveSorting = async () => {
        setSubmitSaveSortingStatus('pending');
        try {
            await sortTasks(rallyId, sortedTasks?.map((task) => task.taskId));
            setSubmitSaveSortingStatus('success');
            setIsSortingChanged(false);
            setSortedTasks(null);
        }
        catch (error) {
            setSubmitSaveSortingStatus('error');
            setIsSortingChanged(false);
            setSortedTasks(null);
            dispatch(setIsModalOpen(true));
            dispatch(setModalContent(MODAL_CONTENT.somethingWentWrong.key));
        }
    };
    const publishRally = async () => {
        try {
            await activateAdminRally(String(rallyId));
            navigate(generateIdRoutePath(APP_ROUTES.adminPublishedRally, rally.uuid));
        }
        catch (error) {
            dispatch(setIsModalOpen(true));
            dispatch(setModalContent(MODAL_CONTENT.somethingWentWrong.key));
        }
    };
    useEffect(() => {
        const handleSaveSorting = async () => {
            await saveSorting();
            if (saveSortingTrigger === 'create-task') {
                navigate(generateIdRoutePath(APP_ROUTES.adminCreateTask, rallyId));
            }
            else if (saveSortingTrigger === 'page-out') {
                navigate(APP_ROUTES.adminManageRallies);
            }
            else if (saveSortingTrigger === 'publish-rally') {
                await publishRally();
            }
            dispatch(setSaveSortingChanges(false));
            dispatch(setSaveSortingTrigger(null));
        };
        if (saveSortingChanges && saveSortingTrigger) {
            handleSaveSorting();
        }
    }, [saveSortingChanges]);
    const handleSorted = (sortedTasks) => {
        setSortedTasks(sortedTasks);
    };
    const returnToRalliesList = () => {
        if (!isSortingChanged) {
            navigate(APP_ROUTES.adminManageRallies);
        }
        else {
            dispatch(setIsModalOpen(true));
            dispatch(setModalContent(MODAL_CONTENT.unsavedSortingChanges.key));
            dispatch(setModalContentProps({
                rallyId,
                saveSortingTrigger: 'page-out',
            }));
        }
    };
    const handleCreateTask = () => {
        if (rally?.hasStatistics) {
            dispatch(setIsModalOpen(true));
            dispatch(setModalContent(MODAL_CONTENT.cannotEditRally.key));
            dispatch(setModalContentProps({ rallyId: rally?.uid }));
            return;
        }
        if (!isSortingChanged) {
            navigate(generateIdRoutePath(APP_ROUTES.adminCreateTask, rallyId));
        }
        else {
            dispatch(setIsModalOpen(true));
            dispatch(setModalContent(MODAL_CONTENT.unsavedSortingChanges.key));
            dispatch(setModalContentProps({
                rallyId,
                saveSortingTrigger: 'create-task',
            }));
        }
    };
    return (_jsx(AdminPageLayout, { children: _jsx("div", { className: 'page-content-wrapper manage-tasks-page', children: _jsx(AsideContentLayout, { mainContent: _jsxs(_Fragment, { children: [_jsxs("div", { className: "manage-tasks-page-header", children: [_jsxs("div", { className: "page-header-column", children: [_jsx(Headline, { title: `${t('admin.manage_tasks')}.`, type: 'h1' }), _jsx(ButtonsGroup, { children: _jsx(Button, { onClick: handleCreateTask, label: t('button.add_task'), icon: _jsx(PlusIcon, {}), iconSize: 'small', disableWithClass: rally?.hasStatistics }) })] }), _jsx("div", { className: "page-header-column", children: _jsx(ShareQR, { url: `${location.origin}/admin/rallies/${rallyId}/rally-preview/1`, description: "admin.share_rally_description", title: "admin.share_rally_title" }) })] }), _jsx(Headline, { title: t('admin.your_tasks'), type: 'h1' }), _jsx("p", { children: t('admin.tasks_sorting_instruction') }), tasks ? (_jsx(AdminTasksList, { rallyId: rallyId, tasks: tasks, onSorted: handleSorted, setIsSortingChanged: setIsSortingChanged, isChangesAllowed: !rally?.hasStatistics })) : (_jsx(Loader, {})), tasks && tasks.length > 7 && !rally?.hasStatistics && (_jsx(ButtonsGroup, { children: _jsx(Button, { onClick: handleCreateTask, label: t('button.add_task'), icon: _jsx(PlusIcon, {}), iconSize: 'small', disableWithClass: rally?.hasStatistics }) })), _jsxs(ButtonsGroup, { children: [!rally?.isActive && (_jsx(Button, { onClick: async () => {
                                        if (!isSortingChanged) {
                                            await publishRally();
                                        }
                                        else {
                                            dispatch(setIsModalOpen(true));
                                            dispatch(setModalContent(MODAL_CONTENT.unsavedSortingChanges.key));
                                            dispatch(setModalContentProps({
                                                rallyId,
                                                saveSortingTrigger: 'publish-rally',
                                            }));
                                        }
                                    }, label: t('button.publish_rally') })), _jsx(Button, { onClick: saveSorting, label: t('button.save_changes'), disabled: !isSortingChanged || submitSaveSortingStatus === 'pending' }), _jsx(Button, { variant: 'outlined', onClick: returnToRalliesList, label: t('button.cancel') })] })] }), asideContent: _jsx(ShareQR, { alert: t('admin.save_changes_to_enable_preview'), url: !isSortingChanged
                        ? `${location.origin}/admin/rallies/${rallyId}/rally-preview/1`
                        : undefined, description: "admin.share_rally_description", title: "admin.share_rally_title" }) }) }) }));
};
export default ManageTasksPage;
